import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import DesktopMockup from "@/components/public/mockups/DesktopMockup";
import type { Flagship } from "@/lib/brand";
import { acquireLoadSlot, releaseLoadSlot } from "./flagshipLoadQueue";

/**
 * Static inline flagship-site preview inside a laptop mockup.
 *
 * Architecture
 * ------------
 * The laptop renders inline in its flagship card (no React portal, no
 * viewport-fixed positioning). It scrolls with the card naturally via the
 * browser's compositor — zero JS per frame. Responsive: sized fluidly up to
 * 360 px on desktop, shrinks to card width on phones; `DesktopMockup`
 * handles the chrome's internal scaling via `aspect-[16/10]`.
 *
 * Iframe lifecycle
 * ----------------
 * 1. IntersectionObserver waits until the laptop enters the viewport, then
 *    requests a slot from the shared `flagshipLoadQueue` (max 2 concurrent
 *    loads across the 8 flagships to avoid saturating the connection pool).
 * 2. Once granted, the `<iframe>` mounts with `src={flagship.url}`.
 * 3. `onload` fires → `handleLoad` classifies the result:
 *      • Cross-origin `SecurityError` reading `location.href`  → `isLive`
 *      • `about:blank` or `chrome-error://`                    → `isBlocked`
 *      • Anything else                                          → `isLive`
 * 4. Retry remounts the iframe (via the `attempt` key) and re-enters the
 *    queue; slow loads (> LOAD_TIMEOUT_MS with no `onload`) surface the
 *    Retry/Visit CTAs without marking the card as blocked.
 *
 * Mobile
 * ------
 * `pointer-events: none` on the iframe below the `md:` breakpoint so a
 * touch-drag always scrolls the page, never hijacks into the embedded site.
 * Mobile visitors use the card's own "Visit site" button (rendered by
 * `FlagshipCard` in [FlagshipsTimeline.tsx]) for full-site navigation.
 */

interface FlagshipInteractivePreviewProps {
    flagship: Flagship;
}

// The iframe is rendered at a real laptop viewport (1280×800) and then
// scaled down to fit the mockup's inner screen so the embedded site picks
// its desktop layout / breakpoints rather than its mobile one.
const IFRAME_WIDTH = 1280;
const IFRAME_HEIGHT = 800;

// Generous cap — real sites over slow connections with redirects + no-cache
// can take well over 10 s to fire `onload`. Primary detection is still
// `onload` itself; this timer only surfaces retry CTAs if the site hangs.
const LOAD_TIMEOUT_MS = 30000;

const FlagshipInteractivePreview = ({ flagship }: FlagshipInteractivePreviewProps) => {
    const { t } = useTranslation();
    const prefersReducedMotion = useReducedMotion();

    // Gate: iframe loading is disabled for `prefers-reduced-motion` users.
    // (No width gate — mobile should see the preview too.)
    const canMount = !prefersReducedMotion;

    const [shouldMount, setShouldMount] = useState(false);
    // Queue grant — iframe only renders once a concurrency slot is available.
    const [hasSlot, setHasSlot] = useState(false);
    const [isLive, setIsLive] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [isSlow, setIsSlow] = useState(false);
    // Bumping `attempt` remounts the iframe for a fresh navigation.
    const [attempt, setAttempt] = useState(0);
    const [screenSize, setScreenSize] = useState({ w: 0, h: 0 });

    const containerRef = useRef<HTMLDivElement>(null);
    const screenRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const timeoutRef = useRef<number | null>(null);
    const holdingSlotRef = useRef(false);

    // Mount the iframe once the laptop is clearly in view. Higher threshold
    // (0.5) means a row has to be roughly half-visible before we start the
    // load — prevents an adjacent `min-h-screen` row from mounting its iframe
    // while the user is still looking at the previous card.
    useEffect(() => {
        if (!canMount) return;
        const el = containerRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldMount(true);
                    io.disconnect();
                }
            },
            { threshold: 0.5, rootMargin: "0px" },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [canMount]);

    // Ask the shared concurrency queue for permission to mount the iframe.
    useEffect(() => {
        if (!shouldMount || hasSlot || isLive || isBlocked) return;
        let cancelled = false;
        const cancelWait = acquireLoadSlot(() => {
            if (cancelled) return;
            holdingSlotRef.current = true;
            setHasSlot(true);
        });
        return () => {
            cancelled = true;
            if (!holdingSlotRef.current) cancelWait();
        };
    }, [shouldMount, hasSlot, isLive, isBlocked, attempt]);

    // Release the slot on unmount if we're still holding one.
    useEffect(
        () => () => {
            if (holdingSlotRef.current) {
                releaseLoadSlot();
                holdingSlotRef.current = false;
            }
        },
        [],
    );

    // Measure the inner laptop screen so the iframe (rendered at 1280×800)
    // can be scaled down to fit. ResizeObserver handles viewport resizes
    // and the responsive width breakpoints automatically.
    useEffect(() => {
        const el = screenRef.current;
        if (!el) return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            setScreenSize({ w: rect.width, h: rect.height });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Safety-net timeout: if `onload` never fires, surface Retry/Visit CTAs
    // but leave the iframe mounted — it may still eventually paint.
    useEffect(() => {
        if (!shouldMount || !hasSlot || isLive || isBlocked) return;
        timeoutRef.current = window.setTimeout(() => {
            if (import.meta.env.DEV) {
                console.warn(
                    `[FlagshipPreview] ${flagship.key}: onload hasn't fired in ${LOAD_TIMEOUT_MS} ms — showing retry UI, iframe still attempting to load.`,
                );
            }
            setIsSlow(true);
        }, LOAD_TIMEOUT_MS);
        return () => {
            if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
        };
    }, [shouldMount, hasSlot, isLive, isBlocked, attempt, flagship.key]);

    const releaseHeldSlot = () => {
        if (holdingSlotRef.current) {
            releaseLoadSlot();
            holdingSlotRef.current = false;
        }
    };

    const handleLoad = () => {
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Positive block signal = same-origin `about:blank` or `chrome-error://`.
        // Everything else is treated as "loaded" — better to show an empty
        // iframe briefly than to false-flag a fine site as blocked.
        let positivelyBlocked = false;
        try {
            const href = iframeRef.current?.contentWindow?.location?.href;
            if (
                typeof href === "string" &&
                (href === "about:blank" || href.startsWith("chrome-error://"))
            ) {
                positivelyBlocked = true;
            }
        } catch {
            // Cross-origin SecurityError → site loaded successfully.
        }

        if (positivelyBlocked) {
            if (import.meta.env.DEV) {
                console.warn(
                    `[FlagshipPreview] ${flagship.key}: blocked (positive signal)`,
                );
            }
            setIsBlocked(true);
            setIsSlow(false);
            releaseHeldSlot();
            return;
        }

        if (import.meta.env.DEV) {
            console.info(`[FlagshipPreview] ${flagship.key}: loaded`);
        }
        setIsLive(true);
        setIsSlow(false);
        releaseHeldSlot();
    };

    const retry = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        releaseHeldSlot();
        setHasSlot(false);
        setIsSlow(false);
        setIsBlocked(false);
        setIsLive(false);
        setAttempt((a) => a + 1);
    };

    // Use `max` (not `min`) so the scaled iframe fully covers the laptop
    // screen. DesktopMockup's inner area isn't exactly 16:10 due to the bezel
    // padding, so `min` leaves a visible gap of the black placeholder on one
    // side. `max` crops a few pixels from the embedded page but delivers the
    // "preview fills the laptop" look the user asked for. Combined with
    // `overflow-hidden` on the parent, nothing spills out of the chrome.
    const iframeScale =
        screenSize.w > 0
            ? Math.max(screenSize.w / IFRAME_WIDTH, screenSize.h / IFRAME_HEIGHT)
            : 0;

    return (
        <div
            ref={containerRef}
            className="w-full max-w-[360px] mx-auto"
        >
            <div
                className={cn(
                    "transition-transform duration-700 ease-out",
                    !isLive && "animate-bob-v2",
                )}
            >
                <DesktopMockup className="w-full">
                    <div
                        ref={screenRef}
                        className="relative w-full h-full overflow-hidden bg-[#0a0a14]"
                    >
                        {/* Branded placeholder — fades out only when the
                            iframe has loaded successfully. */}
                        <div
                            className={cn(
                                "absolute inset-0 flex flex-col items-center justify-center gap-3 transition-opacity duration-500",
                                isLive ? "opacity-0" : "opacity-100",
                            )}
                            style={{
                                background:
                                    "linear-gradient(135deg, hsl(260 90% 18%), hsl(240 33% 6%) 50%, hsl(190 90% 18%))",
                            }}
                        >
                            <span className="font-v2-display text-white/90 text-base md:text-xl tracking-tight">
                                {flagship.name}
                            </span>

                            {/* Pulsing dots while loading (not blocked, not slow). */}
                            {shouldMount && !isBlocked && !isSlow && (
                                <div className="flex gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse" />
                                    <span
                                        className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse"
                                        style={{ animationDelay: "150ms" }}
                                    />
                                    <span
                                        className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse"
                                        style={{ animationDelay: "300ms" }}
                                    />
                                </div>
                            )}

                            {/* Retry + Visit CTAs when blocked or stuck. */}
                            {(isBlocked || isSlow) && (
                                <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                                    <button
                                        type="button"
                                        onClick={retry}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white text-[10px] font-black uppercase tracking-widest border border-white/15 hover:bg-white/20 transition-colors"
                                    >
                                        {t("flagships_home.retry_load")}
                                    </button>
                                    <a
                                        href={flagship.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 text-background text-[10px] font-black uppercase tracking-widest hover:bg-white transition-colors"
                                    >
                                        {t("flagships_home.visit_site")}
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Live iframe. Keyed by `attempt` so Retry remounts
                            it for a fresh navigation. `pointer-events-none`
                            on mobile so touches scroll the page; restored
                            to `auto` at the `md:` breakpoint so desktop
                            visitors can interact with the embedded site. */}
                        {shouldMount && hasSlot && !isBlocked && iframeScale > 0 && (
                            <iframe
                                key={`${flagship.key}-attempt-${attempt}`}
                                ref={iframeRef}
                                title={flagship.name}
                                src={flagship.url}
                                loading="lazy"
                                referrerPolicy="origin"
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                                onLoad={handleLoad}
                                className={cn(
                                    "absolute border-0 bg-white transition-opacity duration-500",
                                    "pointer-events-none md:pointer-events-auto",
                                    isLive ? "opacity-100" : "opacity-0",
                                )}
                                style={{
                                    width: `${IFRAME_WIDTH}px`,
                                    height: `${IFRAME_HEIGHT}px`,
                                    top: "50%",
                                    left: "50%",
                                    transform: `translate(-50%, -50%) scale(${iframeScale})`,
                                }}
                            />
                        )}

                        {/* LIVE chip — appears over a successfully-loaded site. */}
                        {isLive && (
                            <div
                                aria-hidden
                                className="absolute top-2 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
                            >
                                <span className="font-v2-label text-[8px] tracking-widest px-2 py-0.5 rounded-full bg-black/50 text-white/80 backdrop-blur-sm border border-white/10">
                                    LIVE
                                </span>
                            </div>
                        )}
                    </div>
                </DesktopMockup>
            </div>
        </div>
    );
};

export default FlagshipInteractivePreview;
